import { useMemo } from "react";
import Button from "react-bootstrap/Button";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import commonStyles from "../../verification-service-details-component.less";
import { ModalTypes } from "@enums/modal-types";
import { AddRpServiceConfigurationPropertyModal } from "../../../../modals/add-rp-service-configuration-property-modal/add-rp-service-configuration-property-modal";
import { RpServiceConfigurationPropertiesContainer } from "../../../../common/rp-service-configuration-properties-container/rp-service-configuration-properties-container";
import Alert from "react-bootstrap/Alert";
import { INCODE_SMS_DOC_CAPTURE_SO_NAME_PROPERTY_ID } from "@constants/incode-sms-constants";

type ServicePropertiesTabContainerProps = {
    isDisabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    showIncodeFlowTabs: boolean;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const ServicePropertiesTabContainer = (props: ServicePropertiesTabContainerProps) => {
    const { isDisabled, rpServiceConfigurationState, showIncodeFlowTabs, setRpServiceConfigurationState } = props;
    const modalContext = useModalContext();



    const serviceOfferingPropertyIds = useMemo(() => {
        return rpServiceConfigurationState.properties.filter(property => 
            (property.category === "serviceOffering" 
                && !(showIncodeFlowTabs && property.propertyTypeId === INCODE_SMS_DOC_CAPTURE_SO_NAME_PROPERTY_ID))
    
        ).map(property => property.propertyTypeId);
    }, [showIncodeFlowTabs]);

    const unselectedServiceOfferingProperties = rpServiceConfigurationState.properties.filter(property => serviceOfferingPropertyIds.includes(property.propertyTypeId) && !property.addedToService);
    const selectedServiceOfferingProperties = rpServiceConfigurationState.properties.filter(property => serviceOfferingPropertyIds.includes(property.propertyTypeId) && property.addedToService);

    const documentCapturePropertyIds = useMemo(() => {
        return rpServiceConfigurationState.properties.filter(property => property.category === "dcui").map(property => property.propertyTypeId);
    }, []);

    const permissionBasedIsDisabled = isDisabled || !rpServiceConfigurationState.permissions.canModifyRPServiceProperties;
    const unselectedDocumentCaptureProperties = rpServiceConfigurationState.properties.filter(property => documentCapturePropertyIds.includes(property.propertyTypeId) && !property.addedToService);
    const selectedDocumentCaptureProperties = rpServiceConfigurationState.properties.filter(property => documentCapturePropertyIds.includes(property.propertyTypeId) && property.addedToService);

    const addRpServiceConfigurationProperty = (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => property.propertyTypeId === rpServiceConfigurationProperty.propertyTypeId
            ? rpServiceConfigurationProperty
            : property
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const onClickAddServiceOfferingProperty = () => {
        handleRenderAddPropertyModal(unselectedServiceOfferingProperties);
    };

    const onClickAddDocumentCaptureProperty = () => {
        handleRenderAddPropertyModal(unselectedDocumentCaptureProperties);
    };

    const handleRenderAddPropertyModal = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
        const renderAddPropertyModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationPropertyModal
                    rpServiceConfigurationProperties={properties}
                    addRpServiceConfigurationProperty={addRpServiceConfigurationProperty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddPropertyModal,
            modalType: ModalTypes.AddProperty
        });
    };

    const servicePropertiesHeaderButton = (
        (!permissionBasedIsDisabled) ? (
            <Button id="service-properties-add-so-property" disabled={unselectedServiceOfferingProperties.length === 0} onClick={onClickAddServiceOfferingProperty}>
                {resources.pages.verificationServiceDetails.tabs.services.subTabs.common.buttons.addProperty.label}
            </Button>
        ) : null
    );

    const documentCapturePropertiesHeaderButton = (
        (!permissionBasedIsDisabled) ? (
            <Button id="service-properties-add-document-capture-property" disabled={unselectedDocumentCaptureProperties.length === 0} onClick={onClickAddDocumentCaptureProperty}>
                {resources.pages.verificationServiceDetails.tabs.services.subTabs.common.buttons.addProperty.label}
            </Button>
        ) : null
    );


    return (
        <div>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.serviceOfferingProperties.title}
                    headerButton={servicePropertiesHeaderButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>
                        {resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.serviceOfferingProperties.description}
                    </span>
                    <div>
                        {!rpServiceConfigurationState.permissions.canModifyRPServiceProperties && (
                            <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.notAuthorizedToModifyProperties}</Alert>
                        )}
                        {unselectedServiceOfferingProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.allPropertiesMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.serviceOfferingProperties.title
                            )}</Alert>
                        )}
                        {selectedServiceOfferingProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.noPropertiesMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.serviceOfferingProperties.title,
                                permissionBasedIsDisabled
                            )}</Alert>
                        )}
                        {selectedServiceOfferingProperties.length > 0 && (
                            <RpServiceConfigurationPropertiesContainer
                                isDisabled={permissionBasedIsDisabled}
                                selectedRpServiceConfigurationProperties={selectedServiceOfferingProperties}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        )}
                    </div>
                </ContentPageBody>
            </div>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.documentCaptureProperties.title}
                    headerButton={documentCapturePropertiesHeaderButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>
                        {resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.documentCaptureProperties.description}
                    </span>
                    <div>
                        {!rpServiceConfigurationState.permissions.canModifyRPServiceProperties && (
                            <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.notAuthorizedToModifyProperties}</Alert>
                        )}
                        {unselectedDocumentCaptureProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.allPropertiesMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.documentCaptureProperties.title
                            )}</Alert>
                        )}
                        {selectedDocumentCaptureProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.noPropertiesMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.sections.documentCaptureProperties.title,
                                permissionBasedIsDisabled
                            )}</Alert>
                        )}
                        {selectedDocumentCaptureProperties.length > 0 && (
                            <RpServiceConfigurationPropertiesContainer
                                isDisabled={permissionBasedIsDisabled}
                                selectedRpServiceConfigurationProperties={selectedDocumentCaptureProperties}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        )}
                    </div>
                </ContentPageBody>
            </div>
        </div>
    );
};